import React from 'react';
import type { WebsiteResponse, UpdateWebsiteRequest } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill } from '@/shared/components/ui/StatusPill';
import type { StatusPillVariant } from '@/shared/components/ui/StatusPill';
import { useWebsiteImportExport } from './useWebsiteImportExport';

interface WebsiteListItemProps {
  website: WebsiteResponse;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onImport: (id: string, data: UpdateWebsiteRequest) => void;
  onPublish: (id: string) => void;
  onUnpublish: (id: string) => void;
}

const STATUS: Record<string, { label: string; variant: StatusPillVariant }> = {
  draft: { label: 'Rascunho', variant: 'neutral' },
  published: { label: 'Publicado', variant: 'success' },
  archived: { label: 'Arquivado', variant: 'warning' },
};

const formatDate = (value?: Date | string): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Linha de tabela de website (view "Lista"). Padrão-lista travado:
 * checkbox (1ª col) + coluna multi-linha (nome + domínio) + menu ••• (última col).
 */
export const WebsiteListItem: React.FC<WebsiteListItemProps> = ({
  website,
  selected = false,
  onToggleSelect,
  onEdit,
  onDelete,
  onImport,
  onPublish,
  onUnpublish,
}) => {
  const { fileInputRef, handleExport, handleImportClick, handleFileChange } =
    useWebsiteImportExport(website, onImport);

  const status = STATUS[website.status] ?? STATUS.draft;
  const isPublished = website.status === 'published';
  const primaryDomain = website.domains?.[0];

  const actions: CardAction[] = [
    { label: 'Editar', icon: 'edit', onClick: () => onEdit(website.id) },
    isPublished
      ? { label: 'Despublicar', icon: 'eye-off', onClick: () => onUnpublish(website.id) }
      : { label: 'Publicar', icon: 'check-circle', onClick: () => onPublish(website.id) },
    { label: 'Exportar', icon: 'download', onClick: handleExport },
    { label: 'Importar', icon: 'upload', onClick: handleImportClick },
    { label: 'Excluir', icon: 'trash', onClick: () => onDelete(website.id), variant: 'danger' },
  ];

  return (
    <tr
      className="border-b transition-colors"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`website-row-${website.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(website.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`website-check-${website.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Nome + domínio principal */}
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={() => onEdit(website.id)}
          className="min-w-0 text-left"
          data-testid={`website-row-name-${website.id}`}
        >
          <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {website.name}
          </div>
          <div className="text-[11px] font-mono tracking-tight truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {primaryDomain ?? 'Sem domínio vinculado'}
          </div>
        </button>
      </td>

      {/* Status (StatusPill shared — F62) */}
      <td className="px-4 py-3">
        <StatusPill
          variant={status.variant}
          label={status.label}
          data-testid={`website-row-status-${website.id}`}
        />
      </td>

      {/* Modelo */}
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-card-text)' }}>
        {website.modelId}
      </td>

      {/* Domínios (contagem) */}
      <td className="px-4 py-3 text-sm tabular-nums" style={{ color: 'var(--theme-muted-text)' }}>
        {website.domains?.length ?? 0}
      </td>

      {/* Atualizado */}
      <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>
        {formatDate(website.updatedAt ?? website.createdAt)}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`website-row-menu-${website.id}`} />
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          onChange={(e) => { void handleFileChange(e); }}
          className="hidden"
          data-testid={`website-row-import-input-${website.id}`}
        />
      </td>
    </tr>
  );
};

WebsiteListItem.displayName = 'WebsiteListItem';
